import React, { useEffect, useMemo, useState } from "react";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import CardTriggerInvoiceitemUX from "react-lib/apps/HISV3/CLM/CardTriggerInvoiceitemUX";
import { dateToStringBE } from "react-lib/utils/dateUtils";
import { Checkbox } from "semantic-ui-react";
import {
  statusInvoice,
  typeInvoice,
  stagingStatus,
  claimResult,
  State,
  WidthsType,
  GenerateTableHeader,
} from "react-lib/apps/HISV3/CLM/sequence/TriggerInvoiceitem";
import SnackMessage from "react-lib/apps/common/SnackMessage";

// Serializer
import InvoiceItemImproveSerializer from "issara-sdk/types/InvoiceItemImproveSerializer_apps_BIL";

// Types
type CardTriggerInvoiceitemProps = {
  onEvent: (e: any) => any;
  setProp: any;
  runSequence: any;
  // Sequence
  TriggerInvoiceitemSequence: State["TriggerInvoiceitemSequence"];
  masterOptions?: Record<string, any>;
  buttonLoadCheck?: Record<string, any>;
  errorMessage?: Record<string, any>;
  successMessage?: Record<string, any>;
  languageUX?: any;
};

// Const
const CARD_TRIGGER_INVOICEITEM = "CardTriggerInvoiceitem";

const CardTriggerInvoiceitem = (props: CardTriggerInvoiceitemProps) => {
  const [selectedItem, setSelectedItem] = useState<any[]>([]);
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);

  // useEffect
  useEffect(() => {
    props.runSequence({
      sequence: "TriggerInvoiceitem",
      restart: true,
    });
  }, []);

  useEffect(() => {
    if (
      props.errorMessage?.[CARD_TRIGGER_INVOICEITEM] ||
      props.successMessage?.[CARD_TRIGGER_INVOICEITEM]
    ) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data}</div>;
  };

  const columnTable = (data: any, style?: any) => {
    return <div style={{ ...style, marginLeft: "10px" }}>{data}</div>;
  };

  const columnTablePrice = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "flex-end" }}>{data}</div>
  };

  const handleSelectAll = (e: any, v: any) => {
    if (v.checked) {
      let newData = (props.TriggerInvoiceitemSequence?.triggerInvoiceList || []).map(
        (item: any) => item.id
      );

      setSelectedItem(newData);
    } else {
      setSelectedItem([]);
    }
  };

  const handleSearchData = () => {
    props.runSequence({
      sequence: "TriggerInvoiceitem",
      action: "search",
      cardKey: CARD_TRIGGER_INVOICEITEM,
      page: 0,
    });

    setSelectedItem([]);
  };

  const handleClearFilter = () => {
    props.runSequence({
      sequence: "TriggerInvoiceitem",
      action: "clear",
    });

    setSelectedItem([]);
  };

  const handleSaveData = (type: string) => {
    props.runSequence({
      sequence: "TriggerInvoiceitem",
      action: "save",
      cardKey: CARD_TRIGGER_INVOICEITEM,
      type,
      selectedItem,
    });

    setSelectedItem([]);
  };

  const handleChangeData = (_event: any, data: any) => {
    props.setProp(`TriggerInvoiceitemSequence.searchTriggerInvoice.${data.name}`, data?.value);
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`TriggerInvoiceitemSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "TriggerInvoiceitem",
      action: "search",
      cardKey: CARD_TRIGGER_INVOICEITEM,
      [key]: value,
    });

    setSelectedItem([]);
  };

  const handleDisabledTrigger = useMemo(() => {
    if (selectedItem.length === 0) return true;
  }, [selectedItem]);

  const triggerInvoiceData = useMemo(() => {
    return (props.TriggerInvoiceitemSequence?.triggerInvoiceList || []).map((item: any) => {
      return {
        ...item,
        check: columnTableCenter(
          <Checkbox
            checked={selectedItem.includes(item.id)}
            disabled={item.order_ack_by_name}
            onChange={(e: any, v: any) => {
              if (v.checked) {
                setSelectedItem([...selectedItem, item.id]);
              } else {
                let newData = selectedItem.filter((key: any) => key !== item.id);
                setSelectedItem(newData);
              }
            }}
          />,
          { padding: "10px" }
        ),
        ...triggerInvoiceColumns.reduce((data: any, header) => {
          const key = header.accessor;

          if (key === "type") {
            let filterType = typeInvoice.find((type: any) => type.key === item[key])?.text || "";

            data[key] = columnTableCenter(filterType);
          } else if (key === "status") {
            let filterStatus =
              statusInvoice.find((status: any) => status.key === item[key])?.text || "";

            data[key] = columnTableCenter(filterStatus);
          } else if (key === "claim_result") {
            let filterStagingStatus =
              claimResult.find((status: any) => status.value === item[key])?.text || "";

            data[key] = columnTable(filterStagingStatus);
          } else if (key === "staging_status") {
            let filterStagingStatus =
              stagingStatus.find((status: any) => status.value === item[key])?.text || "";

            data[key] = columnTableCenter(filterStagingStatus);
          } else if (key === "division") {
            let filterDivision =
              (props.masterOptions?.division || []).find(
                (status: any) => status.value === item[key]
              )?.text || "";

            data[key] = columnTable(filterDivision);
          } else if (key === "bill_mode") {
            let filterDivision =
              (props.masterOptions?.billMode || []).find(
                (status: any) => status.value === item[key]
              )?.text || "";

            data[key] = columnTable(filterDivision);
          } else if (["created", "edited", "performed"].includes(key)) {
            if (item?.[key]) {
              data[key] = columnTableCenter(dateToStringBE(item[key]));
            } else {
              data[key] = "";
            }
          } else if (["display_name", "pricing_name"].includes(key)) {
            data[key] = columnTable(item[key]);
          } else if (key === "edit_user") {
            data[key] = columnTableCenter(item[key] || "None");
          } else if (priceColumns.includes(key)) {
            data[key] = columnTablePrice(item[key]);
          } else if (key === "payment_ready") {
            data[key] = columnTableCenter(item[key] ? "Yes" : "No");
          } else {
            data[key] = columnTableCenter(item[key]);
          }

          return data;
        }, {}),
        // ...triggerInvoiceKey.reduce((data: any, key: string) => {
        //   if (key === "type") {
        //     let filterType = typeInvoice.find((type: any) => type.key === item[key])?.text || "";

        //     data[key] = columnTableCenter(filterType);
        //   } else if (key === "status") {
        //     let filterStatus =
        //       statusInvoice.find((status: any) => status.key === item[key])?.text || "";

        //     data[key] = columnTableCenter(filterStatus);
        //   } else if (key === "claim_result") {
        //     let filterStagingStatus =
        //       claimResult.find((status: any) => status.value === item[key])?.text || "";

        //     data[key] = columnTable(filterStagingStatus);
        //   } else if (key === "staging_status") {
        //     let filterStagingStatus =
        //       stagingStatus.find((status: any) => status.value === item[key])?.text || "";

        //     data[key] = columnTableCenter(filterStagingStatus);
        //   } else if (key === "division") {
        //     let filterDivision =
        //       (props.masterOptions?.division || []).find(
        //         (status: any) => status.value === item[key]
        //       )?.text || "";

        //     data[key] = columnTable(filterDivision);
        //   } else if (key === "bill_mode") {
        //     let filterDivision =
        //       (props.masterOptions?.billMode || []).find(
        //         (status: any) => status.value === item[key]
        //       )?.text || "";

        //     data[key] = columnTable(filterDivision);
        //   } else if (["created", "edited", "performed"].includes(key)) {
        //     if (item?.[key]) {
        //       data[key] = columnTableCenter(dateToStringBE(item[key]));
        //     } else {
        //       data[key] = "";
        //     }
        //   } else if (["display_name", "pricing_name"].includes(key)) {
        //     data[key] = columnTable(item[key]);
        //   } else if (key === "edit_user") {
        //     data[key] = columnTableCenter(item[key] || "None");
        //   } else {
        //     data[key] = columnTableCenter(item[key]);
        //   }

        //   return data;
        // }, {}),
      };
    });
  }, [
    selectedItem,
    props.TriggerInvoiceitemSequence?.triggerInvoiceList,
    props.masterOptions,
    triggerInvoiceColumns,
  ]);

  return (
    <div>
      {openSnackMessage && (
        <SnackMessage
        onEvent={props.onEvent}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_TRIGGER_INVOICEITEM}`, null);
          props.setProp(`successMessage.${CARD_TRIGGER_INVOICEITEM}`, null);
        }}
        error={props.errorMessage?.[CARD_TRIGGER_INVOICEITEM]}
        success={props.successMessage?.[CARD_TRIGGER_INVOICEITEM]}
        languageUX={props.languageUX}
        />
      )}

      <CardTriggerInvoiceitemUX
        paginatedItems={props.TriggerInvoiceitemSequence?.paginatedItems}
        searchTriggerInvoice={props.TriggerInvoiceitemSequence?.searchTriggerInvoice}
        statusInvoiceOption={statusInvoice}
        typeInvoiceOption={typeInvoice}
        billModeOption={props.masterOptions?.billMode}
        onClickClear={handleClearFilter}
        onChangeData={handleChangeData}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        triggerInvoiceData={triggerInvoiceData}
        triggerInvoiceColumns={[
          {
            Header: (
              <Checkbox
                checked={
                  selectedItem.length ===
                    (props.TriggerInvoiceitemSequence?.triggerInvoiceList || []).length &&
                  (props.TriggerInvoiceitemSequence?.triggerInvoiceList || []).length > 0
                }
                onChange={handleSelectAll}
              />
            ),
            accessor: "check",
            width: 60,
          },
          ...triggerInvoiceColumns,
        ]}
        buttonSeach={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleSearchData}
            // data
            paramKey={`${CARD_TRIGGER_INVOICEITEM}_search`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_TRIGGER_INVOICEITEM}_search`]}
            // config
            color="blue"
            size="medium"
            fluid={true}
            title="Search"
          />
        }
        buttonTrigger={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={() => handleSaveData("trigger")}
            disabled={handleDisabledTrigger}
            // data
            paramKey={`${CARD_TRIGGER_INVOICEITEM}_trigger`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_TRIGGER_INVOICEITEM}_trigger`]}
            // config
            color="blue"
            size="medium"
            fluid={true}
            title="Trigger"
          />
        }
      />
    </div>
  );
};

CardTriggerInvoiceitem.displayName = "CardTriggerInvoiceitem";

export default React.memo(CardTriggerInvoiceitem);

// const triggerInvoiceKey = [
//   "id",
//   "type",
//   "order_type",
//   "order_id",
//   "invoice",
//   "encounter",
//   "display_name",
//   "pricing_name",
//   "pricing_code",
//   "pricing_quantity",
//   "pricing_unit",
//   "division",
//   "bill_mode",
//   "cashonly",
//   "unitprice",
//   "price",
//   "claimable",
//   "discount",
//   "absorb",
//   "absorbable",
//   "paid",
//   "status",
//   "staging_sequence",
//   "claim_result",
//   "edit_user",
//   "created",
//   "edited",
//   "performed",
//   "staging_status",
// ];

const priceColumns: Partial<keyof InvoiceItemImproveSerializer>[] = [
  "cashonly",
  "unitprice",
  "price",
  "claimable",
  "discount",
  "absorb",
  "absorbable",
  "paid",
  "non_claimable",
  "payable",
  "reimbursable",
  "send_claim",
  "patient_claim",
  "pay",
  "drg_discount_amount"
];

const columnWidths: WidthsType = {
  display_name: 200,
  pricing_name: 200,
  pricing_code: 120,
  pricing_quantity: 120,
  division: 180,
  bill_mode: 200,
  staging_sequence: 160,
  claim_result: 160,
  created: 150,
  edited: 150,
  performed: 150,
  staging_status: 150,
  mode_name: 200,
  product: 200,
};

const triggerInvoiceColumns = GenerateTableHeader(columnWidths);
